import {
  PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneOff,
  LogIn, LogOut, Plug, ArrowRightLeft, Copy, Phone, Check,
} from 'lucide-react';
import { useState } from 'react';
import { useSoftphone } from '@/store/softphone';
import type { ActivityItem } from '@/lib/useActivityFeed';
import clsx from 'clsx';

/** Activity kind → icon + renk eşlemesi. Eski 3CX paneldeki Bootstrap
 *  bg-*-dark renkleri yerine Tailwind tone palette + Lucide icons. */
function visualFor(item: ActivityItem) {
  if (item.kind === 'call') {
    if (item.direction === 'missed') {
      return { Icon: PhoneMissed, ring: 'ring-red-200', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' };
    }
    if (item.direction === 'outbound') {
      return { Icon: PhoneOutgoing, ring: 'ring-amber-200', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' };
    }
    return { Icon: PhoneIncoming, ring: 'ring-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' };
  }
  if (item.kind === 'queue') {
    if (item.label.includes('çıktı')) {
      return { Icon: LogOut, ring: 'ring-slate-200', bg: 'bg-slate-50', text: 'text-slate-700', dot: 'bg-slate-400' };
    }
    return { Icon: LogIn, ring: 'ring-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' };
  }
  if (item.kind === 'transfer') {
    return { Icon: ArrowRightLeft, ring: 'ring-blue-200', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' };
  }
  // status
  return { Icon: Plug, ring: 'ring-slate-200', bg: 'bg-slate-50', text: 'text-slate-700', dot: 'bg-slate-400' };
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function fmtDur(sec?: number): string | null {
  if (!sec || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
}

export function ActivityCard({ item }: { item: ActivityItem }) {
  const { dial, state } = useSoftphone();
  const [copied, setCopied] = useState(false);
  const v = visualFor(item);
  const canCallback = item.kind === 'call' && !!item.primary;
  const canCopy = item.kind === 'call' && !!item.primary;

  const onCallback = () => {
    if (state !== 'registered' || !item.primary) return;
    dial(item.primary);
  };

  const onCopy = async () => {
    if (!item.primary) return;
    try {
      await navigator.clipboard.writeText(item.primary);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* */ }
  };

  return (
    <li className="group flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50/80 transition">
      {/* Sol: zaman + ikon */}
      <div className="flex items-center gap-2.5 flex-shrink-0 w-[88px]">
        <span className="text-[11px] font-mono text-slate-500 tabular-nums">{fmtTime(item.startedAt)}</span>
        <div className={clsx('w-7 h-7 rounded-full flex items-center justify-center ring-1', v.ring, v.bg)}>
          <v.Icon size={13} className={v.text} />
        </div>
      </div>

      {/* Orta: pill + açıklama */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className={clsx(
            'inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-semibold uppercase tracking-wide',
            v.bg, v.text,
          )}>
            {item.label}
          </span>
          <span className="text-[13px] text-slate-800 font-medium truncate font-mono">{item.primary}</span>
        </div>
        <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
          {item.secondary && <span>{item.secondary}</span>}
          {item.callSummary && (item.callSummary.muteSec || item.callSummary.holdSec) && (
            <>
              {item.secondary && <span className="text-slate-300">·</span>}
              {item.callSummary.holdSec ? <span>beklemede {fmtDur(item.callSummary.holdSec)}</span> : null}
              {item.callSummary.muteSec ? (
                <>
                  {item.callSummary.holdSec ? <span className="text-slate-300">·</span> : null}
                  <span>sustur {fmtDur(item.callSummary.muteSec)}</span>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Sağ: süre + actions (hover'da görünür) */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {fmtDur(item.durationSec) && (
          <span className="text-[12px] font-mono text-slate-600 tabular-nums mr-1">{fmtDur(item.durationSec)}</span>
        )}
        {canCopy && (
          <button
            onClick={onCopy}
            className="w-7 h-7 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
            title="Numarayı kopyala"
          >
            {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
          </button>
        )}
        {canCallback && (
          <button
            onClick={onCallback}
            disabled={state !== 'registered'}
            className="w-7 h-7 rounded-md text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 flex items-center justify-center opacity-0 group-hover:opacity-100 disabled:opacity-30 disabled:cursor-not-allowed transition"
            title={state !== 'registered' ? 'SIP bağlantısı yok' : 'Geri ara'}
          >
            <Phone size={13} />
          </button>
        )}
        {!canCallback && !canCopy && (
          <span className="text-slate-300"><PhoneOff size={12} className="opacity-0" /></span>
        )}
      </div>
    </li>
  );
}
